import { useState } from "react";
import { supabase } from "./supabaseClient";
import { X, ArrowRight, Building2, Target, BarChart3, FileSearch, CheckCircle2 } from "lucide-react";
import logo from "./logo.png";

const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C+"];
const SECTORS = ["SaaS", "Fintech", "Healthtech", "Edtech", "CleanTech", "E-commerce", "AI/ML", "Biotech", "Consumer", "DeepTech", "Other"];
const TICKET_SIZES = ["Under ₹50L", "₹50L - ₹2Cr", "₹2Cr - ₹10Cr", "₹10Cr - ₹50Cr", "Above ₹50Cr"];

const STEPS = [
  { id: "welcome", title: "Welcome to Binos", icon: null },
  { id: "fund", title: "Your Fund Profile", icon: Building2 },
  { id: "thesis", title: "Your Investment Thesis", icon: Target },
  { id: "done", title: "You're all set", icon: CheckCircle2 },
];

export default function Onboarding({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Fund profile state
  const [fundName, setFundName] = useState("");
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [ticketSize, setTicketSize] = useState("");

  // Thesis state
  const [thesis, setThesis] = useState("");

  const toggleStage = (s) => setSelectedStages(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleSector = (s) => setSelectedSectors(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const handleComplete = async () => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from("profiles").update({
          fund_name: fundName || undefined,
          onboarded: true,
        }).eq("id", session.user.id);
      }
      if (thesis.trim()) {
        try { localStorage.setItem("binos_pitch_criteria", thesis); } catch {}
      }
    } catch (e) { console.error(e); }
    setSaving(false);
    onComplete();
  };

  const handleSkip = () => {
    if (step === STEPS.length - 1) { handleComplete(); return; }
    if (step === STEPS.length - 2) { handleComplete(); return; }
    setStep(prev => prev + 1);
  };

  const handleNext = () => {
    if (step === STEPS.length - 1) { handleComplete(); return; }
    setStep(prev => prev + 1);
  };

  const progress = ((step) / (STEPS.length - 1)) * 100;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, width: "100%", maxWidth: 520, overflow: "hidden", position: "relative" }}>

        {/* Progress bar */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.06)", position: "absolute", top: 0, left: 0, right: 0 }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "#dc2626", transition: "width 0.4s ease", borderRadius: 100 }} />
        </div>

        {/* Skip button */}
        {step < STEPS.length - 1 && (
          <button onClick={handleSkip}
            style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6, transition: "color 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.6)"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.3)"}>
            Skip <X style={{ width: 12, height: 12 }} />
          </button>
        )}

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 6, padding: "20px 28px 0", justifyContent: "center" }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ width: i === step ? 20 : 6, height: 6, borderRadius: 100, background: i === step ? "#dc2626" : i < step ? "rgba(220,38,38,0.4)" : "rgba(255,255,255,0.1)", transition: "all 0.3s ease" }} />
          ))}
        </div>

        <div style={{ padding: "24px 32px 32px" }}>

          {/* ── STEP 0: Welcome ── */}
          {step === 0 && (
            <div style={{ textAlign: "center" }}>
              <img src={logo} style={{ width: 52, height: 52, margin: "0 auto 20px" }} alt="Binos" />
              <h2 style={{ fontSize: 26, fontWeight: 900, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.5px" }}>
                Welcome to Binos{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
              </h2>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", margin: "0 0 32px", lineHeight: 1.7 }}>
                Your AI-powered operating system for venture capital. Let's get your fund set up in 2 minutes.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 32, textAlign: "left" }}>
                {[
                  { icon: FileSearch, label: "Pitch Deck Analyser", desc: "60 seconds per deck" },
                  { icon: Target, label: "Thesis Matcher", desc: "Score deals automatically" },
                  { icon: BarChart3, label: "Portfolio Health", desc: "Track every company" },
                  { icon: Building2, label: "Investment Memo", desc: "AI-powered memos" },
                ].map((f, i) => (
                  <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px" }}>
                    <f.icon style={{ width: 16, height: 16, color: "#dc2626", marginBottom: 8 }} />
                    <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>{f.label}</p>
                    <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>{f.desc}</p>
                  </div>
                ))}
              </div>

              <button onClick={handleNext}
                style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}>
                Let's get started <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          )}

          {/* ── STEP 1: Fund Profile ── */}
          {step === 1 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <Building2 style={{ width: 18, height: 18, color: "#dc2626" }} />
                <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.15em", margin: 0 }}>Step 1 of 2</p>
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>Your Fund Profile</h2>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 24px" }}>Tell us about your fund so Binos can personalise your experience.</p>

              {/* Fund name */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 8 }}>Fund Name</label>
                <input value={fundName} onChange={e => setFundName(e.target.value)} placeholder="e.g. Sequoia India, Blume Ventures..."
                  style={{ width: "100%", padding: "11px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 14, fontWeight: 500, color: "#fff", fontFamily: "inherit", boxSizing: "border-box" }}
                  onFocus={e => e.target.style.borderColor = "#dc2626"}
                  onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
              </div>

              {/* Stage focus */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 8 }}>Stage Focus <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(select all that apply)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {STAGES.map(s => (
                    <button key={s} onClick={() => toggleStage(s)}
                      style={{ padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 600, border: "1px solid", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", background: selectedStages.includes(s) ? "rgba(220,38,38,0.15)" : "transparent", borderColor: selectedStages.includes(s) ? "#dc2626" : "rgba(255,255,255,0.12)", color: selectedStages.includes(s) ? "#dc2626" : "rgba(255,255,255,0.5)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sector focus */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 8 }}>Sector Focus <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(select all that apply)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SECTORS.map(s => (
                    <button key={s} onClick={() => toggleSector(s)}
                      style={{ padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 600, border: "1px solid", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", background: selectedSectors.includes(s) ? "rgba(220,38,38,0.15)" : "transparent", borderColor: selectedSectors.includes(s) ? "#dc2626" : "rgba(255,255,255,0.12)", color: selectedSectors.includes(s) ? "#dc2626" : "rgba(255,255,255,0.5)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ticket size */}
              <div style={{ marginBottom: 28 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 8 }}>Typical Ticket Size</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {TICKET_SIZES.map(s => (
                    <button key={s} onClick={() => setTicketSize(s)}
                      style={{ padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 600, border: "1px solid", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", background: ticketSize === s ? "rgba(220,38,38,0.15)" : "transparent", borderColor: ticketSize === s ? "#dc2626" : "rgba(255,255,255,0.12)", color: ticketSize === s ? "#dc2626" : "rgba(255,255,255,0.5)" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={handleNext}
                style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}>
                Continue <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          )}

          {/* ── STEP 2: Thesis ── */}
          {step === 2 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <Target style={{ width: 18, height: 18, color: "#dc2626" }} />
                <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.15em", margin: 0 }}>Step 2 of 2</p>
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>Your Investment Thesis</h2>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 20px", lineHeight: 1.6 }}>
                Describe what your fund looks for. Binos will score every pitch deck against this automatically.
              </p>

              <textarea value={thesis} onChange={e => setThesis(e.target.value)} rows={6}
                placeholder="e.g. We invest in B2B SaaS companies at Seed and Series A stage in India. We look for technical founding teams with strong domain expertise, early revenue traction above ₹50L ARR, and a clear path to ₹10Cr ARR within 18 months..."
                style={{ width: "100%", padding: "14px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, outline: "none", fontSize: 13, fontWeight: 400, color: "#fff", resize: "none", fontFamily: "inherit", lineHeight: 1.7, boxSizing: "border-box", marginBottom: 12 }}
                onFocus={e => e.target.style.borderColor = "#dc2626"}
                onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
                {["B2B SaaS, Seed/Series A, India", "Consumer fintech, pre-seed, Tier 2 cities", "Deep tech, Series A, global market"].map((ex, i) => (
                  <button key={i} onClick={() => setThesis(ex)}
                    style={{ padding: "5px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(220,38,38,0.3)"; e.currentTarget.style.color = "#dc2626"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.35)"; }}>
                    "{ex}"
                  </button>
                ))}
              </div>

              <button onClick={handleNext}
                style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit" }}>
                Save & Continue <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          )}

          {/* ── STEP 3: Done ── */}
          {step === 3 && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 64, height: 64, background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <CheckCircle2 style={{ width: 32, height: 32, color: "#16a34a" }} />
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 900, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.5px" }}>You're all set!</h2>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.45)", margin: "0 0 32px", lineHeight: 1.7 }}>
                Binos is ready. Start by uploading a pitch deck or adding your first portfolio company.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                {[
                  { label: "Upload a pitch deck", desc: "Analyse it in 60 seconds", tab: "Pitch Summariser" },
                  { label: "Add a portfolio company", desc: "Start tracking your investments", tab: "Fund Health" },
                  { label: "Build a metric bundle", desc: "Set your custom benchmarks", tab: "Financial Calculator" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12 }}>
                    <div style={{ textAlign: "left" }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>{item.label}</p>
                      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: 0 }}>{item.desc}</p>
                    </div>
                    <ArrowRight style={{ width: 14, height: 14, color: "rgba(255,255,255,0.2)" }} />
                  </div>
                ))}
              </div>

              <button onClick={handleComplete} disabled={saving}
                style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : "Go to Dashboard →"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
